import DataTable from "datatables.net-dt";
import "datatables.net-buttons/js/buttons.html5.mjs";
import "datatables.net-buttons/js/buttons.print.mjs";
import "datatables.net-buttons/js/buttons.colVis.mjs";
import "datatables.net-colreorder-dt";
import "datatables.net-responsive-dt";
import "datatables.net-rowgroup-dt";
import "datatables.net-fixedheader-dt";
import "datatables.net-dt/css/datatables.dataTables.css";
import "datatables.net-buttons-dt/css/buttons.dataTables.css";
import "datatables.net-colreorder-dt/css/colreorder.dataTables.css";
import "datatables.net-responsive-dt/css/responsive.dataTables.css";
import "datatables.net-rowgroup-dt/css/rowgroup.dataTables.css";
import "datatables.net-fixedheader-dt/css/fixedheader.dataTables.css";

import * as utils from "./basin-reports-utils.js";
import { getBasinData, getRefData } from "../model/get-data.js";

export const tableFnLookup = {
    fcst: makeFcstTable,
    prec: makePrecTable,
    snow: makeSnowTable,
    res: makeResTable,
};

export function isHiddenRow(hideNullStations, ...checkVals) {
    if (!hideNullStations) {
        return false;
    }
    for (const value of checkVals) {
        if (value || value === 0) {
            return false;
        }
    }
    return true;
}

export function makeTable(
    element,
    elementDiv,
    elementData,
    basinName,
    hideNullStations,
    primaryForecastsOnly,
    funcLookup = tableFnLookup,
) {
    const elementMakeTable = funcLookup[element];
    elementMakeTable(elementDiv, elementData, basinName, hideNullStations, primaryForecastsOnly);
}

export async function createBasinTables(basinName, basinType) {
    const hideNullStations = document.getElementById("switch--hide-missing-data").checked || false;
    const primaryForecastsOnly = document.getElementById("switch--only-primary-forecasts").checked || false;
    utils.reportElements.forEach((element) => {
        let elementData = getBasinData(element)[basinName] || [{ site_meta: {} }];
        elementData = utils.getBasinType(elementData, basinType);
        const elementDiv = document.getElementById(element);
        const hasData = !utils.wsorDataEmpty(elementData);
        hasData && makeTable(element, elementDiv, elementData, basinName, hideNullStations, primaryForecastsOnly);
        utils.setReportToggles(element, hasData);
    });
    utils.adjustHeaders();
}

export function addDataTableFunctions(tableId, numRows, basinName, rowGroups = false) {
    const table = `#${tableId}`;
    const useScroll = document.getElementById("radio-display--scroll").checked;
    const rowGroupsOptions = rowGroups ? { dataSrc: 0 } : false;
    const tableOptions = getTableOptions(tableId, basinName, numRows, rowGroups, useScroll, rowGroupsOptions);
    window[tableId] = new DataTable(table, tableOptions);
}

export function getTableOptions(tableId, basinName, numRows, rowGroups, useScroll, rowGroupsOptions) {
    const tableNameMap = {
        precTable: "Precipitation Table",
        snowTable: "Snowpack Table",
        resTable: "Reservoir Table",
        fcstTable: "Streamflow Forecast Table",
    };
    const siteTypeMap = {
        precTable: "Station",
        snowTable: "Station",
        resTable: "Reservoir",
        fcstTable: "Streamflow Forecast",
    };
    const exportFilename = utils.convertToValidFilename(
        `${basinName}_${utils.getMonth()}_${utils.getYear()}_${tableNameMap[tableId]}`,
    );
    const footerText = `_TOTAL_ ${siteTypeMap[tableId]}${numRows > 1 ? "s" : ""} in ${basinName.toUpperCase()}`;
    const fixedHeaderOptions = numRows > 10 ? { header: true, footer: true } : false;
    const scrollOptions = useScroll ? { scrollY: "300px", scrollCollapse: true } : { fixedHeader: fixedHeaderOptions };
    return {
        ...scrollOptions,
        order: [],
        paging: false,
        colReorder: tableId === "fcstTable" ? true : false,
        responsive: { details: { renderer: responsiveRenderer } },
        rowGroup: rowGroupsOptions,
        columnDefs: [{ targets: 0, visible: !rowGroups }],
        layout: {
            bottomEnd: { buttons: getExportButtons(tableId, tableNameMap, exportFilename) },
            bottomStart: { info: { text: footerText } },
        },
        initComplete: styleExportButtons,
    };
}

export function styleExportButtons() {
    const exportBtns = document.querySelectorAll(`button.dt-button.buttons-collection`);
    exportBtns.forEach((exportBtn) => {
        exportBtn.classList.add("fsa-btn");
        exportBtn.classList.add("fsa-btn--secondary");
        exportBtn.classList.remove("dt-button");
        exportBtn.classList.remove("buttons-collection");
    });
}

export function responsiveRenderer(api, idx, columns) {
    const tableId = api.table().node().id;
    let header = api.table().header.structure();
    header.forEach((headerRow, rowIdx) => {
        if (rowIdx < header.length - 1) {
            let headerFreeze = { title: "" };
            headerRow.forEach((headerCell, cellIdx) => {
                if (cellIdx > 0 && headerCell) {
                    headerFreeze = headerCell;
                } else {
                    header[rowIdx][cellIdx] = headerFreeze;
                }
            });
        }
    });

    const data = columns.map((col) => {
        if (col.hidden) {
            let headerPrefix = tableId === "fcstTable" ? "" : `${header[0][col.columnIndex].title}`;
            if (tableId === "precTable") {
                const precTypeHeader = header[0][col.columnIndex].title.split(" ");
                const precTypeAbbr = precTypeHeader.includes("Monthly")
                    ? precTypeHeader[precTypeHeader.length - 1]
                    : "to Date";
                headerPrefix = `${header[1][col.columnIndex].title} ${precTypeAbbr}`;
            }
            headerPrefix = headerPrefix ? `${headerPrefix} - ` : headerPrefix;
            const detail = /*html*/ `
                <tr data-dt-row="${col.rowIndex}" data-dt-column="${col.columnIndex}">
                    <td>${headerPrefix.replace("Water Year ", "WY ")}${col.title}:</td>
                    <td>${col.data}</td>
                </tr>`;

            return detail;
        } else {
            return "";
        }
    });

    const table = document.createElement("table");
    table.innerHTML = data.join("");

    return data ? table : false;
}

function getExportButtons(tableId, tableNameMap, exportFilename) {
    return [
        {
            extend: "collection",
            text: `Export ${tableNameMap[tableId]}`,
            buttons: [
                { extend: "copyHtml5", filename: exportFilename, title: "" },
                { extend: "csvHtml5", filename: exportFilename, title: "" },
                { extend: "pdfHtml5", filename: exportFilename, title: "", customize: centerHeaderExport },
                { extend: "excelHtml5", filename: exportFilename, title: "" },
                "print",
            ],
        },
    ];
}

export function centerHeaderExport(doc) {
    doc.defaultStyle.alignment = "center";
    doc.styles.tableHeader.alignment = "center";
}

export function getNetworksFootnoteElement(usedNetworks) {
    const footnote = document.createElement("div");
    footnote.className = "fsa-text-size--1";
    const textArr = [];
    usedNetworks.forEach((network) => {
        textArr.push(`<b>${network}:</b> ${getRefData("networks")[network].description}`);
    });
    footnote.innerHTML = textArr.join("<br>");
    return footnote;
}

export function makeSnowTable(div, data, basinName, hideNullStations = false) {
    utils.removeDataTableFunctions("snowTable");
    div.innerHTML = "";
    const table = document.createElement("table");
    table.id = "snowTable";
    table.className = "display compact hover row-border stripe order-column";
    const caption = document.createElement("caption");
    caption.innerHTML = `Snowpack Summary for ${utils.getLongMonth()} 1, ${utils.getYear()}`;
    caption.className = "fsa-text-size--4@l";
    table.appendChild(caption);
    const thead = document.createElement("thead");
    table.appendChild(thead);
    const wy = utils.getWaterYear();
    const theadHTML = /*html*/ `
		<tr>
			<th colspan="4" scope="col" class="dt-head-left">${basinName.toUpperCase()}</th>
			<th colspan="3" scope="col" class="dt-head-center">Water Year ${wy}</th>
			<th colspan="2" scope="col" class="dt-head-center">Water Year ${wy - 1}</th>
		</tr>
		<tr>
			<th scope="col" class="dt-head-left">Site</th>
			<th scope="col" class="dt-head-center">Network</th>
			<th scope="col" class="dt-head-center">Elevation (ft)</th>
			<th scope="col" class="dt-head-center">Median SWE (in)</th>
			<th scope="col" class="dt-head-center">Snow Depth (in)</th>

			<th scope="col" class="dt-head-center">SWE (in)</th>
			<th scope="col" class="dt-head-center">% Median SWE</th>

			<th scope="col" class="dt-head-center">SWE (in)</th>
			<th scope="col" class="dt-head-center">% Median SWE</th>
		</tr>`;

    thead.innerHTML = theadHTML;
    const tbody = document.createElement("tbody");
    let numRows = 0;
    const usedNetworks = [];
    Object.keys(data.site_meta || {}).forEach((trip) => {
        const meta = data.site_meta?.[trip] || {};
        if (!usedNetworks.includes(meta.network)) {
            usedNetworks.push(meta.network.toUpperCase());
        }
        const currWteq = data.wteq_curr?.[trip];
        const lastYearWteq = data.wteq_ly?.[trip];
        const medianWteq = data.wteq_med?.[trip];
        const currSnowDepth = data.snwd_curr?.[trip];
        const currPercent = medianWteq && currWteq ? Math.round((100 * currWteq) / medianWteq) : "--";
        const lyPercent = medianWteq && lastYearWteq ? Math.round((100 * lastYearWteq) / medianWteq) : "--";
        const rowHTML = /*html*/ `
			<td class="dt-body-left">${meta.name || "--"}</td>
			<td class="dt-body-center">${meta.network || "--"}</td>
			<td class="dt-body-center">${meta.elevation || "--"}</td>
			<td class="dt-body-center">${medianWteq || medianWteq === 0 ? medianWteq : "--"}</td>
			<td class="dt-body-center">${currSnowDepth || currSnowDepth === 0 ? currSnowDepth : "--"}</td>
			<td class="dt-body-center">${currWteq || currWteq === 0 ? currWteq : "--"}</td>
			<td class="dt-body-center">${currPercent || currPercent === 0 ? currPercent : "--"}</td>
			<td class="dt-body-center">${lastYearWteq || lastYearWteq === 0 ? lastYearWteq : "--"}</td>
			<td class="dt-body-center">${lyPercent || lyPercent === 0 ? lyPercent : "--"}</td>`;

        if (!isHiddenRow(hideNullStations, currWteq, lastYearWteq)) {
            const tr = document.createElement("tr");
            tr.innerHTML = rowHTML;
            tbody.appendChild(tr);
            numRows += 1;
        }
    });
    const networksFootnote = getNetworksFootnoteElement(usedNetworks);
    const basinIndex = data.basin_index || {};
    const tfoot = document.createElement("tfoot");
    const trIndex = document.createElement("tr");
    tfoot.appendChild(trIndex);
    const indexHTML = /*html*/ `
		<td colspan="6" class="dt-head-left fsa-font--sans-bold">Basin Index</td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.wteq_curr_per_med || basinIndex.wteq_curr_per_med === 0 ? basinIndex.wteq_curr_per_med : "--"}
        </td>
		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.wteq_ly_per_med || basinIndex.wteq_ly_per_med === 0 ? basinIndex.wteq_ly_per_med : "--"}
        </td>`;

    trIndex.innerHTML = indexHTML;
    const trCnt = document.createElement("tr");
    tfoot.appendChild(trCnt);
    const cntHTML = /*html*/ `
		<td colspan="6" class="dt-head-left fsa-font--sans-bold"># Sites</td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.wteq_curr_cnt || basinIndex.wteq_curr_cnt === 0 ? basinIndex.wteq_curr_cnt : "--"}
        </td>
		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.wteq_ly_cnt || basinIndex.wteq_ly_cnt === 0 ? basinIndex.wteq_ly_cnt : "--"}
        </td>`;

    trCnt.innerHTML = cntHTML;
    table.appendChild(tbody);
    table.appendChild(tfoot);
    div.appendChild(table);
    div.appendChild(networksFootnote);
    addDataTableFunctions("snowTable", numRows, basinName);
}

export function makePrecTable(div, data, basinName, hideNullStations = false) {
    utils.removeDataTableFunctions("precTable");
    div.innerHTML = "";
    const table = document.createElement("table");
    table.id = "precTable";
    table.className = "display compact hover row-border stripe order-column";
    const caption = document.createElement("caption");
    caption.innerHTML = `Precipitation Summary for ${utils.getLongMonth()} 1, ${utils.getYear()} `;
    caption.className = "fsa-text-size--4@l";
    table.appendChild(caption);
    const thead = document.createElement("thead");
    table.appendChild(thead);
    const wy = utils.getWaterYear();
    const lastMonth = utils.getLongLastMonth();
    const theadHTML = /*html*/ `
        <tr>
			<th colspan="3" scope="col" class="dt-head-left">${basinName.toUpperCase()}</th>
			<th colspan="5" scope="col" class="dt-head-center">Water Year Precipitation through ${lastMonth}</th>
			<th colspan="5" scope="col" class="dt-head-center">Monthly Precipitation for ${lastMonth}</th>
		</tr >
		<tr>
			<th colspan="4" scope="col" class="dt-head-center"></th>
			<th colspan="2" scope="col" class="dt-head-center">Water Year ${wy}</th>
			<th colspan="2" scope="col" class="dt-head-center">Water Year ${wy - 1}</th>
			<th colspan="1" scope="col" class="dt-head-center"></th>
			<th colspan="2" scope="col" class="dt-head-center">Water Year ${wy}</th>
			<th colspan="2" scope="col" class="dt-head-center">Water Year ${wy - 1}</th>
		</tr>
		<tr>
			<th scope="col" class="dt-head-left">Site</th>
			<th scope="col" class="dt-head-center">Network</th>
			<th scope="col" class="dt-head-center">Elevation (ft)</th>
			<th scope="col" class="dt-head-center">Median (in)</th>
			<th scope="col" class="dt-head-center">Obs. (in)</th>
			<th scope="col" class="dt-head-center">% Median</th>
			<th scope="col" class="dt-head-center">Obs. (in)</th>
			<th scope="col" class="dt-head-center">% Median</th>
			<th scope="col" class="dt-head-center">Median (in)</th>
			<th scope="col" class="dt-head-center">Obs. (in)</th>
			<th scope="col" class="dt-head-center">% Median</th>
			<th scope="col" class="dt-head-center">Obs. (in)</th>
			<th scope="col" class="dt-head-center">% Median</th>
		</tr>`;

    thead.innerHTML = theadHTML;
    const tbody = document.createElement("tbody");
    let numRows = 0;
    const usedNetworks = [];
    Object.keys(data.site_meta || {}).forEach((trip) => {
        const meta = data.site_meta?.[trip];
        if (!usedNetworks.includes(meta.network)) {
            usedNetworks.push(meta.network.toUpperCase());
        }
        const medianYearToDatePrec = data.prec_ytd_med?.[trip];
        const currYearToDatePrec = data.prec_ytd_curr?.[trip];
        const lastYearYearToDatePrec = data.prec_ytd_ly?.[trip];
        const medianMonthlyPrec = data.prec_mnth_med?.[trip];
        const currMonthlyPrec = data.prec_mnth_curr?.[trip];
        const lastYearMonthlyPrec = data.prec_mnth_ly?.[trip];
        const currPercentWY =
            medianYearToDatePrec && currYearToDatePrec
                ? Math.round((100 * currYearToDatePrec) / medianYearToDatePrec)
                : "--";
        const lyPercentWY =
            medianYearToDatePrec && lastYearYearToDatePrec
                ? Math.round((100 * lastYearYearToDatePrec) / medianYearToDatePrec)
                : "--";
        const currPercentMonthly =
            medianMonthlyPrec && currMonthlyPrec ? Math.round((100 * currMonthlyPrec) / medianMonthlyPrec) : "--";
        const lyPercentMonthly =
            medianMonthlyPrec && lastYearMonthlyPrec
                ? Math.round((100 * lastYearMonthlyPrec) / medianMonthlyPrec)
                : "--";
        const rowHTML = /*html*/ `
            <td class="dt-body-left"> ${meta.name || "--"}</td >
			<td class="dt-body-center">${meta.network || "--"}</td>
			<td class="dt-body-center">${meta.elevation || "--"}</td>

			<td class="dt-body-center">${medianYearToDatePrec || medianYearToDatePrec === 0 ? medianYearToDatePrec : "--"}</td>
			<td class="dt-body-center">${currYearToDatePrec || currYearToDatePrec === 0 ? currYearToDatePrec : "--"}</td>
			<td class="dt-body-center">${currPercentWY || currPercentWY === 0 ? currPercentWY : "--"}</td>

			<td class="dt-body-center">${lastYearYearToDatePrec || lastYearYearToDatePrec === 0 ? lastYearYearToDatePrec : "--"}</td>
			<td class="dt-body-center">${lyPercentWY || lyPercentWY === 0 ? lyPercentWY : "--"}</td>

			<td class="dt-body-center">${medianMonthlyPrec || medianMonthlyPrec === 0 ? medianMonthlyPrec : "--"}</td>
			<td class="dt-body-center">${currMonthlyPrec || currMonthlyPrec === 0 ? currMonthlyPrec : "--"}</td>
			<td class="dt-body-center">${currPercentMonthly || currPercentMonthly === 0 ? currPercentMonthly : "--"}</td>

			<td class="dt-body-center">${lastYearMonthlyPrec || lastYearMonthlyPrec === 0 ? lastYearMonthlyPrec : "--"}</td>
			<td class="dt-body-center">${lyPercentMonthly || lyPercentMonthly === 0 ? lyPercentMonthly : "--"}</td>`;

        if (
            !isHiddenRow(
                hideNullStations,
                currYearToDatePrec,
                currMonthlyPrec,
                lastYearYearToDatePrec,
                lastYearMonthlyPrec,
            )
        ) {
            const tr = document.createElement("tr");
            tr.innerHTML = rowHTML;
            tbody.appendChild(tr);
            numRows += 1;
        }
    });
    const networksFootnote = getNetworksFootnoteElement(usedNetworks);
    const basinIndex = data.basin_index || {};
    const tfoot = document.createElement("tfoot");
    const trIndex = document.createElement("tr");
    tfoot.appendChild(trIndex);
    const indexHTML = /*html*/ `
        <td colspan = "5" class="dt-head-left fsa-font--sans-bold" > Basin Index</td>

		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.prec_ytd_curr_per_med || basinIndex.prec_ytd_curr_per_med === 0 ? basinIndex.prec_ytd_curr_per_med : "--"}
        </td>
		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.prec_ytd_ly_per_med || basinIndex.prec_ytd_ly_per_med === 0 ? basinIndex.prec_ytd_ly_per_med : "--"}
        </td>

		<td colspan="2" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.prec_mnth_curr_per_med || basinIndex.prec_mnth_curr_per_med === 0 ? basinIndex.prec_mnth_curr_per_med : "--"}
        </td>
		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.prec_mnth_ly_per_med || basinIndex.prec_mnth_ly_per_med === 0 ? basinIndex.prec_mnth_ly_per_med : "--"}
        </td>`;

    trIndex.innerHTML = indexHTML;
    const trCnt = document.createElement("tr");
    tfoot.appendChild(trCnt);
    const cntHTML = /*html*/ `
        <td colspan = "3" class="dt-head-left fsa-font--sans-bold" ># Sites</td>

		<td colspan="2" class="dt-head-center fsa-font--sans-bold"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.prec_ytd_curr_cnt || basinIndex.prec_ytd_curr_cnt === 0 ? basinIndex.prec_ytd_curr_cnt : "--"}
        </td>
		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.prec_ytd_ly_cnt || basinIndex.prec_ytd_ly_cnt === 0 ? basinIndex.prec_ytd_ly_cnt : "--"}
        </td>

		<td colspan="2" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.prec_mnth_curr_cnt || basinIndex.prec_mnth_curr_cnt === 0 ? basinIndex.prec_mnth_curr_cnt : "--"}
        </td>
		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.prec_mnth_ly_cnt || basinIndex.prec_mnth_ly_cnt === 0 ? basinIndex.prec_mnth_ly_cnt : "--"}
        </td>`;

    trCnt.innerHTML = cntHTML;
    table.appendChild(tbody);
    table.appendChild(tfoot);
    div.appendChild(table);
    div.appendChild(networksFootnote);
    addDataTableFunctions("precTable", numRows, basinName);
}

export function makeResTable(div, data, basinName, hideNullStations = false) {
    utils.removeDataTableFunctions("resTable");
    div.innerHTML = "";
    const table = document.createElement("table");
    table.id = "resTable";
    table.className = "display compact hover row-border stripe order-column";
    const caption = document.createElement("caption");
    caption.innerHTML = `Reservoir Summary for for ${utils.getLongMonth()} 1, ${utils.getYear()} `;
    caption.className = "fsa-text-size--4@l";
    table.appendChild(caption);
    const thead = document.createElement("thead");
    table.appendChild(thead);
    const wy = utils.getWaterYear();
    const theadHTML = /*html*/ `
        <tr>
			<th colspan="5" scope="col" class="dt-head-left">${basinName.toUpperCase()}</th>
			<th colspan="3" scope="col" class="dt-head-center">Water Year ${wy}</th>
			<th colspan="3" scope="col" class="dt-head-center">Water Year ${wy - 1}</th>
		</tr >
        <tr>
            <th scope="col" class="dt-head-left">Site</th>
            <th scope="col" class="dt-head-center">Elevation (ft)</th>
            <th scope="col" class="dt-head-center">Capacity (kaf)</th>
            <th scope="col" class="dt-head-center">Median (kaf)</th>
            <th scope="col" class="dt-head-center">Median % Capacity</th>

            <th scope="col" class="dt-head-center">Storage (kaf)</th>
            <th scope="col" class="dt-head-center">% Capacity</th>
            <th scope="col" class="dt-head-center">% Median</th>

            <th scope="col" class="dt-head-center">Storage (kaf)</th>
            <th scope="col" class="dt-head-center">% Capacity</th>
            <th scope="col" class="dt-head-center">% Median</th>
        </tr>`;

    thead.innerHTML = theadHTML;
    const tbody = document.createElement("tbody");
    let numRows = 0;
    Object.keys(data.site_meta || {}).forEach((trip) => {
        const meta = data.site_meta?.[trip] || {};
        const resCap = data.res_cap?.[trip];
        const resMedian = data.res_med?.[trip];
        const currResStorage = data.res_curr?.[trip];
        const latYearResStorage = data.res_ly?.[trip];
        const medPercentCap = resCap && resMedian ? Math.round((100 * resMedian) / resCap) : "--";
        const currPercentCap = resCap && currResStorage ? Math.round((100 * currResStorage) / resCap) : "--";
        const currPercentMed = resMedian && currResStorage ? Math.round((100 * currResStorage) / resMedian) : "--";
        const lyPercentCap = resCap && latYearResStorage ? Math.round((100 * latYearResStorage) / resCap) : "--";
        const lyPercentMed = resMedian && latYearResStorage ? Math.round((100 * latYearResStorage) / resMedian) : "--";
        const rowHTML = /*html*/ `
            <td class="dt-body-left" > ${meta.name || "--"}</td>
			<td class="dt-body-center">${meta.elevation || "--"}</td>
			<td class="dt-body-center">${resCap || resCap === 0 ? resCap : "--"}</td>
			<td class="dt-body-center">${resMedian || resMedian === 0 ? resMedian : "--"}</td>
			<td class="dt-body-center">${medPercentCap || medPercentCap === 0 ? medPercentCap : "--"}</td>

			<td class="dt-body-center">${currResStorage || currResStorage === 0 ? currResStorage : "--"}</td>
			<td class="dt-body-center">${currPercentCap || currPercentCap === 0 ? currPercentCap : "--"}</td>
			<td class="dt-body-center">${currPercentMed || currPercentMed === 0 ? currPercentMed : "--"}</td>

			<td class="dt-body-center">${latYearResStorage || latYearResStorage === 0 ? latYearResStorage : "--"}</td>
			<td class="dt-body-center">${lyPercentCap || lyPercentCap === 0 ? lyPercentCap : "--"}</td>
			<td class="dt-body-center">${lyPercentMed || lyPercentMed === 0 ? lyPercentMed : "--"}</td>`;

        if (!isHiddenRow(hideNullStations, currResStorage, latYearResStorage)) {
            const tr = document.createElement("tr");
            tr.innerHTML = rowHTML;
            tbody.appendChild(tr);
            numRows += 1;
        }
    });
    const basinIndex = data.basin_index || {};
    const tfoot = document.createElement("tfoot");
    const trIndex = document.createElement("tr");
    tfoot.appendChild(trIndex);
    const indexHTML = /*html*/ `
        <td colspan = "5" class="dt-head-left fsa-font--sans-bold" > Basin Index</td>

		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.res_curr_per_cap || basinIndex.res_curr_per_cap === 0 ? basinIndex.res_curr_per_cap : "--"}
        </td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.res_curr_per_med || basinIndex.res_curr_per_med === 0 ? basinIndex.res_curr_per_med : "--"}
        </td>

		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.res_ly_per_cap || basinIndex.res_ly_per_cap === 0 ? basinIndex.res_ly_per_cap : "--"}
        </td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.res_ly_per_med || basinIndex.res_ly_per_med === 0 ? basinIndex.res_ly_per_med : "--"}
        </td>`;

    trIndex.innerHTML = indexHTML;
    const trCnt = document.createElement("tr");
    tfoot.appendChild(trCnt);
    const cntHTML = /*html*/ `
        <td colspan = "5" class="dt-head-left fsa-font--sans-bold" ># Reservoirs</td>

		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.res_curr_cap_cnt || basinIndex.res_curr_cap_cnt === 0 ? basinIndex.res_curr_cap_cnt : "--"}
        </td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.res_curr_cnt || basinIndex.res_curr_cnt === 0 ? basinIndex.res_curr_cnt : "--"}
        </td>

		<td colspan="1" class="dt-head-center"></td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.res_ly_cap_cap || basinIndex.res_ly_cap_cap === 0 ? basinIndex.res_ly_cap_cap : "--"}
        </td>
		<td colspan="1" class="dt-head-center fsa-font--sans-bold">
            ${basinIndex.res_ly_cnt || basinIndex.res_ly_cnt === 0 ? basinIndex.res_ly_cnt : "--"}
        </td>`;

    trCnt.innerHTML = cntHTML;
    table.appendChild(tbody);
    table.appendChild(tfoot);
    div.appendChild(table);
    addDataTableFunctions("resTable", numRows, basinName);
}

export function getForecastFootnotes(hasExceedanceFootnote = false) {
    const footnotes = document.createElement("div");
    footnotes.className = "fsa-text-size--1";
    const htmlArr = [];
    if (hasExceedanceFootnote) {
        htmlArr.push("<b>1:</b> 90% and 10% Exceedance Probabilities are actually 95% and 5%, respectively.");
    }
    const sacLink = document.createElement("a");
    sacLink.className = "fsa-link:hover--underline-none";
    sacLink.href = "https://www.wcc.nrcs.usda.gov/ftpref/support/sac_eq/";
    sacLink.innerHTML = "Streamflow Adjustment Equations";
    sacLink.target = "_blank";
    const sacFootnote = `For information about how adjusted streamflow datasets are calculated visit: ${sacLink.outerHTML} `;
    htmlArr.push(sacFootnote);
    footnotes.innerHTML = htmlArr.join("<br>");
    return footnotes;
}

export function makeFcstTable(div, data, basinName, hideNullStations = false, primaryForecastsOnly = false) {
    utils.removeDataTableFunctions("fcstTable");
    div.innerHTML = "";
    const table = document.createElement("table");
    table.id = "fcstTable";
    table.className = "display compact hover row-border stripe order-column";
    const caption = document.createElement("caption");
    caption.innerHTML = `Streamflow Forecast Summary for ${utils.getLongMonth()} 1, ${utils.getYear()} `;
    caption.className = "fsa-text-size--4@l";
    table.appendChild(caption);
    const thead = document.createElement("thead");
    table.appendChild(thead);
    const fcstNormalType = document.getElementById("radio-use-fcst--median").checked ? "med" : "avg";
    const fcstNormalTypeLabel = fcstNormalType === "med" ? "Median" : "Average";
    const theadHTML = /*html*/ `
        <tr>
			<th colspan="2" scope="col" class="dt-head-left">${basinName.toUpperCase()}</th>
			<th colspan="7" scope="col" class="dt-head-center">Forecast Exceedance Probabilities For Risk Assessment</th>
		</tr >
        <tr>
            <th scope="col" class="dt-head-left">Site</th>
            <th scope="col" class="dt-head-center">Forecast Period</th>
            <th scope="col" class="dt-head-center">${fcstNormalTypeLabel} (kaf)</th>

            <th scope="col" class="dt-head-center">90% (kaf)</th>
            <th scope="col" class="dt-head-center">70% (kaf)</th>
            <th scope="col" class="dt-head-center">50% (kaf)</th>
            <th scope="col" class="dt-head-center">50% (as % ${fcstNormalTypeLabel})</th>
            <th scope="col" class="dt-head-center">30% (kaf)</th>
            <th scope="col" class="dt-head-center">10% (kaf)</th>
        </tr>`;

    thead.innerHTML = theadHTML;
    const tbody = document.createElement("tbody");
    let hasExceedanceFootnote = false;
    let numRows = 0;
    Object.keys(data.site_meta || {}).forEach((trip) => {
        const meta = data.site_meta?.[trip] || {};
        const fcstPeriods = Object.keys(data.fcst_curr?.[trip] || { "APR-JUL": "" });
        const stationPrimaryPeriod = data.primary_periods?.[trip] || "APR-JUL";
        let periodCnt = 0;
        if (fcstPeriods.length) {
            fcstPeriods.forEach((fcstPeriod) => {
                if (stationPrimaryPeriod == fcstPeriod || !primaryForecastsOnly) {
                    const normalsKey = `fcst_${fcstNormalType}`.trim();
                    const fcstPeriodCurrValues = data.fcst_curr?.[trip]?.[fcstPeriod];
                    const fcstPeriodMiddle = fcstPeriodCurrValues?.["50"];
                    const upperExceedance = fcstPeriodCurrValues?.["90"] ? "90" : "95";
                    const lowerExceedance = fcstPeriodCurrValues?.["10"] ? "10" : "5";
                    const fcstPeriodNormal = data[normalsKey]?.[trip]?.[fcstPeriod];
                    const percentNormal =
                        fcstPeriodMiddle && fcstPeriodNormal
                            ? Math.round((100 * fcstPeriodMiddle) / fcstPeriodNormal)
                            : "--";
                    if (upperExceedance === "95" || lowerExceedance == "5") {
                        hasExceedanceFootnote = true;
                    }
                    const exceedanceFootnote = upperExceedance === "90" ? "" : "<sup>1</sup>";

                    const rowHTML = /*html*/ `
                        <td class="dt-body-left" > ${meta.name || "--"}</td>
                        <td class="dt-body-center">${fcstPeriod}${exceedanceFootnote}</td>
                        <td class="dt-body-center">
                            ${fcstPeriodNormal || fcstPeriodNormal === 0 ? fcstPeriodNormal : "--"}
                        </td>
                        <td class="dt-body-center">
                            ${fcstPeriodCurrValues?.[upperExceedance] || fcstPeriodCurrValues?.[upperExceedance] === 0 ? fcstPeriodCurrValues?.[upperExceedance] : "--"}
                        </td>
                        <td class="dt-body-center">
                            ${fcstPeriodCurrValues?.["70"] || fcstPeriodCurrValues?.["70"] === 0 ? fcstPeriodCurrValues?.["70"] : "--"}
                        </td>
                        <td class="dt-body-center">
                            ${fcstPeriodCurrValues?.["50"] || fcstPeriodCurrValues?.["50"] === 0 ? fcstPeriodCurrValues?.["50"] : "--"}
                        </td>
                        <td class="dt-body-center">
                            ${percentNormal || percentNormal === 0 ? percentNormal : "--"}
                        </td>
                        <td class="dt-body-center">
                            ${fcstPeriodCurrValues?.["30"] || fcstPeriodCurrValues?.["30"] === 0 ? fcstPeriodCurrValues?.["30"] : "--"}
                        </td>
                        <td class="dt-body-center">
                            ${fcstPeriodCurrValues?.[lowerExceedance] || fcstPeriodCurrValues?.[lowerExceedance] === 0 ? fcstPeriodCurrValues?.[lowerExceedance] : "--"}
                        </td>`;

                    if (!isHiddenRow(hideNullStations, fcstPeriodMiddle)) {
                        const tr = document.createElement("tr");
                        tr.innerHTML = rowHTML;
                        tbody.appendChild(tr);
                        periodCnt += 1;
                    }
                }
            });
        }
        numRows += 1;
    });
    table.appendChild(tbody);
    div.appendChild(table);
    const footnotes = getForecastFootnotes(hasExceedanceFootnote);
    div.appendChild(footnotes);
    addDataTableFunctions("fcstTable", numRows, basinName, true);
}
